import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listMembers,
  updateMemberRole,
  removeMember,
  listInvitations,
  createInvitation,
  revokeInvitation,
  getErrorMessage,
  type WorkspaceRole,
  type Member,
  type Invitation,
} from '@/lib/api';
import { PageHeader, Icon, Badge, EmptyState, Loading, ConfirmDialog } from '@/components/ui';

const ROLES: WorkspaceRole[] = ['owner', 'editor', 'analyst', 'viewer'];
const ROLE_LABEL: Record<WorkspaceRole, string> = {
  owner: 'Propriétaire',
  editor: 'Éditeur',
  analyst: 'Analyste',
  viewer: 'Lecteur',
};
const ROLE_DESC: Record<WorkspaceRole, string> = {
  owner: 'Contrôle total : membres, suppression, diffusion.',
  editor: 'Crée et édite les enquêtes.',
  analyst: 'Accède aux réponses agrégées.',
  viewer: 'Consultation seule.',
};

/**
 * Page Membres & invitations (B7c). Réservée owner (le backend applique le RBAC ;
 * l'UI reflète les actions disponibles). Invitation par e-mail + lien copiable
 * (fonctionne même sans SMTP), gestion des rôles, révocation.
 */
export default function MembersPage(): JSX.Element {
  const { id: workspaceId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('analyst');
  const [lastInvite, setLastInvite] = useState<{ acceptUrl: string; emailSent: boolean; email: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'removeMember'; member: Member } | { kind: 'revokeInvite'; inv: Invitation } | null>(null);

  const membersQ = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => listMembers(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
  });
  const invitesQ = useQuery({
    queryKey: ['invitations', workspaceId],
    queryFn: () => listInvitations(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['members', workspaceId] });
    void qc.invalidateQueries({ queryKey: ['invitations', workspaceId] });
  };

  const inviteMut = useMutation({
    mutationFn: () => createInvitation(workspaceId ?? '', email.trim().toLowerCase(), role),
    onSuccess: (res) => {
      setLastInvite({ acceptUrl: res.acceptUrl, emailSent: res.emailSent, email: email.trim().toLowerCase() });
      setEmail('');
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(getErrorMessage(err)),
  });

  const roleMut = useMutation({
    mutationFn: (v: { membershipId: string; role: WorkspaceRole }) =>
      updateMemberRole(workspaceId ?? '', v.membershipId, v.role),
    onSuccess: invalidate,
    onError: (err) => setFormError(getErrorMessage(err)),
  });

  const removeMut = useMutation({
    mutationFn: (membershipId: string) => removeMember(workspaceId ?? '', membershipId),
    onSuccess: () => { invalidate(); setConfirm(null); },
    onError: (err) => { setFormError(getErrorMessage(err)); setConfirm(null); },
  });

  const revokeMut = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(workspaceId ?? '', invitationId),
    onSuccess: () => { invalidate(); setConfirm(null); },
    onError: (err) => { setFormError(getErrorMessage(err)); setConfirm(null); },
  });

  if (!workspaceId) return <p>Workspace introuvable.</p>;

  const pending = (invitesQ.data ?? []).filter((i) => i.status === 'pending');

  return (
    <>
      <PageHeader
        title="Membres & partage"
        subtitle="Invitez des collaborateurs par e-mail et gérez leurs rôles. Le partage d'une enquête précise se fait depuis le menu de chaque enquête."
        back={{ to: `/workspaces/${workspaceId}/surveys`, label: 'Retour aux enquêtes' }}
      />

      {formError && (
        <p role="alert" className="field-error mb-4"><Icon.AlertTriangle /><span>{formError}</span></p>
      )}

      {/* Invitation */}
      <section className="card card-flush mb-6">
        <header className="card-header">
          <div className="flex items-center gap-2">
            <Icon.Mail className="text-primary" />
            <h2 className="text-base font-semibold">Inviter un collaborateur</h2>
          </div>
        </header>
        <form
          className="card-section flex flex-col sm:flex-row gap-3 sm:items-end"
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) inviteMut.mutate(); }}
        >
          <div className="field flex-1">
            <label htmlFor="inv-email" className="field-label">Adresse e-mail</label>
            <input id="inv-email" type="email" required className="input-base" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="collegue@institution.fr" />
          </div>
          <div className="field">
            <label htmlFor="inv-role" className="field-label">Rôle</label>
            <select id="inv-role" className="input-base" value={role}
              onChange={(e) => setRole(e.target.value as WorkspaceRole)}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={inviteMut.isPending}>
            {inviteMut.isPending ? 'Envoi…' : 'Inviter'}
          </button>
        </form>
        {lastInvite && (
          <div className="card-section bg-success-soft/40">
            <p className="text-sm text-success-text font-medium">
              <Icon.Check className="inline align-text-bottom mr-1" />
              Invitation créée pour {lastInvite.email}{lastInvite.emailSent ? ' — e-mail envoyé.' : ' — e-mail non envoyé (SMTP absent), copiez le lien :'}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly className="input-base text-xs font-mono flex-1" value={lastInvite.acceptUrl}
                onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="btn-secondary btn-sm"
                onClick={() => void navigator.clipboard.writeText(lastInvite.acceptUrl)}>
                <Icon.Copy /> Copier
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Membres */}
      <section className="card card-flush mb-6">
        <header className="card-header"><h2 className="text-base font-semibold">Membres</h2></header>
        {membersQ.isPending && <div className="card-section"><Loading /></div>}
        {membersQ.data && (
          <ul>
            {membersQ.data.map((m) => (
              <li key={m.membershipId} className="row items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.email}</p>
                  <p className="text-xs text-text-muted">{ROLE_DESC[m.role]}</p>
                </div>
                <select
                  className="input-base text-sm w-40"
                  value={m.role}
                  onChange={(e) => roleMut.mutate({ membershipId: m.membershipId, role: e.target.value as WorkspaceRole })}
                  aria-label={`Rôle de ${m.email}`}
                >
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
                <button type="button" className="btn-ghost btn-sm text-danger-text"
                  onClick={() => setConfirm({ kind: 'removeMember', member: m })}>
                  <Icon.Trash /> Retirer
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Invitations en attente */}
      <section className="card card-flush">
        <header className="card-header"><h2 className="text-base font-semibold">Invitations en attente ({pending.length})</h2></header>
        {invitesQ.isPending && <div className="card-section"><Loading /></div>}
        {invitesQ.data && pending.length === 0 && (
          <div className="card-section">
            <EmptyState icon={<Icon.Mail size={32} />} title="Aucune invitation en attente"
              description="Les invitations envoyées et non encore acceptées apparaissent ici." />
          </div>
        )}
        {pending.length > 0 && (
          <ul>
            {pending.map((inv) => (
              <li key={inv.id} className="row items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.email}</p>
                  <p className="text-xs text-text-muted">
                    Expire le {new Date(inv.expiresAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <Badge tone="primary">{ROLE_LABEL[inv.role]}</Badge>
                <Badge tone="notice">En attente</Badge>
                <button type="button" className="btn-ghost btn-sm text-danger-text"
                  onClick={() => setConfirm({ kind: 'revokeInvite', inv })}>
                  Révoquer
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirm?.kind === 'removeMember'}
        tone="danger"
        title="Retirer ce membre ?"
        description={confirm?.kind === 'removeMember'
          ? <>Le membre <strong>{confirm.member.email}</strong> perdra l'accès à ce workspace. Son compte global est conservé.</>
          : undefined}
        confirmLabel="Retirer"
        busy={removeMut.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.kind === 'removeMember' && removeMut.mutate(confirm.member.membershipId)}
      />
      <ConfirmDialog
        open={confirm?.kind === 'revokeInvite'}
        tone="danger"
        title="Révoquer cette invitation ?"
        description={confirm?.kind === 'revokeInvite'
          ? <>L'invitation pour <strong>{confirm.inv.email}</strong> ne sera plus utilisable.</>
          : undefined}
        confirmLabel="Révoquer"
        busy={revokeMut.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.kind === 'revokeInvite' && revokeMut.mutate(confirm.inv.id)}
      />
    </>
  );
}
